/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef, useState } from 'react';
import { Button, Spinner } from 'reactstrap';
import PropTypes from 'prop-types';

function SpyScraperButton({
  onData, parser, children, style, ...buttonProps
}) {
  const [spyDataAvailable, setSpyDataAvailable] = useState(false);
  const [spyData, setSpyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const activeRequestIdRef = useRef(null);

  useEffect(() => {
    const handleStatus = (event) => {
      if (event.source !== window || event.data?.type !== 'SPYSCRAPER_STATUS') return;

      const requestedSource = parser || null;
      if ((event.data.requestedSource || null) !== requestedSource) return;

      setSpyDataAvailable(!!event.data.ready);
      setSpyData(event.data.preview || null);
    };

    const handleData = async (event) => {
      if (event.source !== window || event.data?.type !== 'SPYSCRAPER_DATA') return;

      const requestedSource = parser || null;
      const incomingSource = event.data.source || null;
      const incomingRequestId = event.data.requestId || null;

      if (requestedSource && incomingSource !== requestedSource) return;
      if (activeRequestIdRef.current !== incomingRequestId) return;

      try {
        await onData?.(event.data.entity);
      } finally {
        activeRequestIdRef.current = null;
        setLoading(false);
        setSpyDataAvailable(false);
      }
    };

    const check = () => {
      window.postMessage(
        parser ? { type: 'SPYSCRAPER_CHECK', source: parser } : { type: 'SPYSCRAPER_CHECK' },
        '*',
      );
    };

    check();
    window.addEventListener('message', handleStatus);
    window.addEventListener('message', handleData);

    return () => {
      window.removeEventListener('message', handleStatus);
      window.removeEventListener('message', handleData);
    };
  }, [onData, parser]);

  useEffect(() => {
    const onFocus = () => {
      window.postMessage(
        parser ? { type: 'SPYSCRAPER_CHECK', source: parser } : { type: 'SPYSCRAPER_CHECK' },
        '*',
      );
    };

    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [parser]);

  const triggerExtension = () => {
    if (loading) return;

    const requestId = `spy_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    activeRequestIdRef.current = requestId;
    setLoading(true);

    window.postMessage(
      parser
        ? { type: 'SPYSCRAPER_DUMP', source: parser, requestId }
        : { type: 'SPYSCRAPER_DUMP', requestId },
      '*',
    );
  };

  if (!spyDataAvailable && !loading) return null;

  return (
    <div className="mt-2 mb-4">
      <Button
        size="sm"
        onClick={triggerExtension}
        disabled={loading}
        style={{ ...style }}
        {...buttonProps}
      >
        {loading ? (
          <>
            <Spinner size="sm" style={{ marginRight: 8 }} />
            Importing...
          </>
        ) : (
          children || (spyData ? (
            <>
              <strong>{parser?.toUpperCase()}</strong>
              <br />
              <span>{spyData.name || 'Unnamed product'}</span>
            </>
          ) : 'Import from SpyScraper')
        )}
      </Button>
    </div>
  );
}

SpyScraperButton.propTypes = {
  onData: PropTypes.func,
  parser: PropTypes.string,
  children: PropTypes.node,
  style: PropTypes.object,
};

SpyScraperButton.defaultProps = {
  onData: null,
  parser: null,
  children: null,
  style: {},
};

export default SpyScraperButton;
